import styled, { createGlobalStyle } from "styled-components";
import confetti from "canvas-confetti";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";

import { FlexCol, FlexRow } from "@ui/flex/flex";
import { OpenInANewTab } from "@ui/open-in-a-new-tab";

import { HeroImage } from "./hero-image";
import { Loader } from "./loader";

const admissionUrl =
  "https://docs.google.com/forms/d/e/1FAIpQLScDJryYWjnTajPxB6H6lKNsCOdB4lZ404he_Cv2M67uivRmXg/viewform?embedded=true";

const BodyStyles = createGlobalStyle`
  body {
    max-width: unset;
  }

  @media (max-width: 1000px) {
    html {
      font-size: 85%;
    }
  }

  @media (max-width: 800px) {
    html {
      font-size: 60%;
    }
  }
`;

const SubTitle = styled.span`
  font-family: "Okticon";
  font-size: 1.8rem;

  @media (max-width: 460px) {
    font-size: 1.6rem;
  }
`;
const Title = styled.span`
  font-family: "Okticon";
  font-weight: 700;
  font-size: 4.4rem;

  margin-top: 0.4rem;

  word-break: keep-all;
  text-align: center;

  background: linear-gradient(120deg, #1565c0 0%, #42a5f5 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 460px) {
    font-size: 3.8rem;
  }
`;

const TitleWrapper = styled(FlexCol)`
  align-items: center;
  margin-top: 1.6rem;
`;

const DateLocationWrapper = styled(FlexRow)`
  margin-top: 2rem;

  column-gap: 4rem;

  @media (max-width: 520px) {
    flex-direction: column;
    row-gap: 0.4rem;
  }
`;

const InfoLabel = styled.span`
  font-family: "Okticon";
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  vertical-align: super;
  font-size: 1.2rem;

  color: rgb(100, 130, 180);
`;
const InfoValue = styled.span`
  font-family: "Okticon";
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: 1.5rem;
`;
const Label = styled.span`
  font-family: "Okticon";
  font-size: 1.4rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};

  text-align: center;
  margin-top: 4.4rem;
  margin-bottom: 1.2rem;

  @media (max-width: 800px) {
    font-size: 1.6rem;
  }
`;
const Sup = styled.span`
  vertical-align: super;
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;
const OpenFormInNewTab = styled(OpenInANewTab)`
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: gray;

  @media (max-width: 800px) {
    font-size: 1.2rem;
  }
`;
const IFrame = styled.iframe`
  display: block;

  margin: 0 auto;

  width: 740px;
  max-width: 100%;
`;

const BiblePhrase = styled.div`
  font-family: "Okticon";
  font-size: 1.3rem;
  line-height: 2rem;

  background: rgba(255, 255, 255, 0.25);
  border-radius: 1.2rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  padding: 1.2rem 9rem;
  margin-top: 4rem;
  text-align: center;
  color: #333;

  @media (max-width: 600px) {
    font-size: 1.1rem;
    padding: 1.2rem 2.4rem;
  }
`;

const snowflakeShapes: confetti.Shape[] = ["circle", "circle", "star"];

const _ApplyPage = ({ className }: { className?: string }) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const isMobile = useMediaQuery({
    query: "(max-width: 1100px)",
  });

  useEffect(() => {
    let animationId: number;
    let startTime: number | null = null;

    const spawnSnow = (x: number) => {
      confetti({
        particleCount: 2,
        angle: 270,
        spread: 30,
        startVelocity: 1,
        decay: 0.95,
        gravity: 0.4,
        drift: Math.random() * 2 - 1,
        origin: { x, y: 0 },
        shapes: snowflakeShapes,
        scalar: isMobile ? 0.8 : 1.0,
        colors: ["#FFFFFF", "#E8F4F8", "#B8D4E3", "#A8C8DC", "#D0E8F2"],
        ticks: 400,
      });
    };

    const animate = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }

      if (startTime) {
        const currentTime = timestamp - startTime;
        if (currentTime > 150) {
          startTime = timestamp;
          spawnSnow(Math.random());
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    const observer = new MutationObserver(() => {
      const canvas = document.querySelector("canvas");
      if (canvas) {
        canvas.style.cssText =
          "position:fixed; left:0; top:0; pointer-events:none; z-index:9999;";
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(animationId);
      observer.disconnect();
    };
  }, [isMobile]);

  return (
    <div className={className}>
      <BodyStyles />
      <HeroImage />
      <TitleWrapper>
        <SubTitle>2026 두모갓 청소년부 겨울수련회</SubTitle>
        <Title>일어나 함께가자</Title>
      </TitleWrapper>
      <DateLocationWrapper>
        <FlexRow columnGap="0.4rem">
          <InfoLabel>일시</InfoLabel>
          <InfoValue>
            2월 6일<sub>(금)</sub> - 7일<sub>(토)</sub>
          </InfoValue>
        </FlexRow>
        <FlexRow columnGap="0.4rem">
          <InfoLabel>장소</InfoLabel>
          <InfoValue>두모갓교회 · 서울숲 · 눈썰매장</InfoValue>
        </FlexRow>
      </DateLocationWrapper>
      <BiblePhrase>
        나의 사랑하는 자가 내게 말하여 이르기를
        <br />
        나의 사랑, 내 어여쁜 자야{" "}
        <b style={{ color: "inherit" }}>일어나서 함께 가자</b>
        <div
          style={{
            color: "inherit",
            fontWeight: 400,
            fontSize: "1.2rem",
            marginTop: "0.8rem",
          }}
        >
          📖 아가 2:10
        </div>
      </BiblePhrase>
      <FlexCol style={{ width: "100%" }}>
        <Label>
          참가신청 <Sup>~1/18(일)까지</Sup>
          <br />
          <OpenFormInNewTab href={admissionUrl}>
            신청서가 보이지 않나요?{" "}
            <ArrowTopRightOnSquareIcon style={{ width: "1rem" }} width={12} />
          </OpenFormInNewTab>
        </Label>
        <div style={{ position: "relative" }}>
          <AnimatePresence>
            {isIframeLoading && (
              <motion.div
                initial={{ opacity: 1 }}
                animate={["opacity"]}
                transition={{ duration: 0.8 }}
                exit={{
                  opacity: 0,
                }}
              >
                <Loader />
              </motion.div>
            )}
          </AnimatePresence>
          <IFrame
            src={admissionUrl}
            title="2026 청소년부 겨울수련회 신청"
            height={2600}
            scrolling="no"
            frameBorder={0}
            marginWidth={0}
            marginHeight={0}
            onLoad={() => setIsIframeLoading(false)}
          />
        </div>
      </FlexCol>
    </div>
  );
};

export const ApplyPage = styled(_ApplyPage)`
  ${HeroImage} {
    width: 28rem;
    max-width: 75%;
  }

  /* background-color: #e4e8ec; */

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 4.8rem 3.2rem;

  @media (max-width: 600px) {
    zoom: 1.2;
    padding: 3.2rem 0;
  }
`;
